import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { PhoneInput } from 'react-international-phone'
import { useProfile } from '@/api/profile'
import { OnboardingNavyShell } from '@/components/onboarding/NavyShell'
import { PhoneVerification, type EtapeCode } from '@/components/onboarding/PhoneVerification'
import { defaultLocale } from '@/lib/locales'

/**
 * Page « Vérifiez votre cellulaire », seule étape demandée à un membre invité
 * (et à tout compte récent qui arrive au dashboard sans l'avoir faite).
 * Le garde OnboardingGuard y envoie tant que le profil dit phone_verification_required.
 */
export function OnboardingPhonePage() {
  const { locale = defaultLocale } = useParams<{ locale: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const queryClient = useQueryClient()
  const { data: profile } = useProfile()
  const [phone, setPhone] = useState('')
  const [etape, setEtape] = useState<EtapeCode>('idle')

  useEffect(() => {
    if (profile?.phone_number && !phone) setPhone(profile.phone_number)
  }, [profile, phone])

  const [sortie, setSortie] = useState(false)
  const suite = async () => {
    // Le garde relit le profil : on attend qu'il soit à jour avant de partir, sinon il nous renvoie ici.
    setSortie(true)
    await queryClient.refetchQueries({ queryKey: ['profile'] })
    navigate(params.get('next') || `/${locale}/dashboard`, { replace: true })
  }

  const champ = { backgroundColor: '#112240', border: '1px solid rgba(255,255,255,0.1)', color: 'white' }

  return (
    <OnboardingNavyShell>
      <main className="flex-grow flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md text-center">
          <h1 className="text-4xl font-bold mb-4 tracking-tight">{t('onboarding.welcome.verif.title')}</h1>

          <div className="space-y-4 text-left">
            <div className="flex flex-col gap-2">
              <label className="text-xs font-bold tracking-widest px-1" style={{ color: '#94A3B8' }}>
                {t('onboarding.welcome.phoneLabel')}
              </label>
              <PhoneInput
                defaultCountry="ca"
                disabled={etape !== 'idle'}
                value={phone}
                onChange={setPhone}
                style={{ '--react-international-phone-height': '48px' } as React.CSSProperties}
                inputStyle={{ ...champ, borderLeft: 'none', width: '100%', fontSize: '14px', borderRadius: '0 8px 8px 0' }}
                countrySelectorStyleProps={{
                  buttonStyle: { ...champ, borderRight: 'none', borderRadius: '8px 0 0 8px', paddingLeft: '8px', paddingRight: '8px' },
                  dropdownStyleProps: { style: { backgroundColor: '#112240', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: 'white' } },
                }}
              />
              {etape === 'ok' && <span className="text-xs" style={{ color: '#34D399' }}>{t('onboarding.welcome.verif.verified')}</span>}
            </div>

            <PhoneVerification phone={phone} locale={locale} etape={etape} onEtape={setEtape} />

            <div className="pt-4">
              <button
                type="button"
                onClick={() => void suite()}
                disabled={etape !== 'ok' || sortie}
                className="w-full h-14 font-bold rounded-lg transition-all flex items-center justify-center text-base disabled:opacity-50"
                style={{ backgroundColor: 'white', color: '#0A192F' }}
              >
                {t('onboarding.welcome.continue')}
              </button>
            </div>
          </div>
        </div>
      </main>
    </OnboardingNavyShell>
  )
}
